import maplibregl from 'maplibre-gl';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRoutellersMapStyle } from '../../utils/app-map-style';

import 'maplibre-gl/dist/maplibre-gl.css';

export type ConstructorMapAttraction = {
    placeKey: string;
    establishment?: boolean;
    title: string;
    lat: number | null;
    lng: number | null;
    reviewStatus: string;
    archived: boolean;
};

export type ConstructorMapStop = {
    key: string;
    order: number;
    kind: 'attraction' | 'waypoint';
    optionalBreak?: boolean;
    fieldExcluded?: boolean;
    placeKey?: string | null;
    lat: number;
    lng: number;
    title?: string;
};

type Props = {
    center: { lat: number; lng: number };
    attractions: ConstructorMapAttraction[];
    stops: ConstructorMapStop[];
    geometry?: [number, number][];
    selectedPlaceKey?: string | null;
    selectedStopKey?: string | null;
    /** Highlight search hits on the map. */
    searchActive?: boolean;
    highlightedPlaceKeys?: string[];
    highlightedStopKeys?: string[];
    /** Clicks select stops. Points cannot be moved or added. */
    readOnly?: boolean;
    onClearSelection: () => void;
    onSelectPlace: (placeKey: string) => void;
    onSelectStop: (stopKey: string) => void;
    onAddWaypoint: (lat: number, lng: number) => void;
    onMovePoint: (
        kind: 'stop' | 'place',
        key: string,
        lat: number,
        lng: number,
    ) => Promise<void>;
};

const statusColor = (status: string) => {
    if (status === 'approved') return '#16A34A';
    return '#F59E0B';
};

const isPoolVisible = (status: string, archived: boolean) =>
    !archived && status !== 'rejected' && status !== 'rejected_mcp';

export const ConstructorMap: React.FC<Props> = ({
    center,
    attractions,
    stops,
    geometry,
    selectedPlaceKey,
    selectedStopKey,
    searchActive = false,
    highlightedPlaceKeys = [],
    highlightedStopKeys = [],
    readOnly = false,
    onClearSelection,
    onSelectPlace,
    onSelectStop,
    onAddWaypoint,
    onMovePoint,
}) => {
    const { t, i18n } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [contextMenu, setContextMenu] = useState<{
        x: number;
        y: number;
        lat: number;
        lng: number;
    } | null>(null);
    const mapRef = useRef<maplibregl.Map | null>(null);
    const markersRef = useRef<maplibregl.Marker[]>([]);
    const draggingRef = useRef(false);
    const suppressClickUntilRef = useRef(0);
    const lastFitKeyRef = useRef('');
    const [mapReady, setMapReady] = useState(false);
    const [dragRevision, setDragRevision] = useState(0);
    const [mapError, setMapError] = useState(false);
    const callbacksRef = useRef({
        onClearSelection,
        onSelectPlace,
        onSelectStop,
        onAddWaypoint,
        onMovePoint,
        readOnly,
    });
    callbacksRef.current = {
        onClearSelection,
        onSelectPlace,
        onSelectStop,
        onAddWaypoint,
        onMovePoint,
        readOnly,
    };
    useEffect(() => {
        if (!contextMenu) return;
        const dismiss = (e: PointerEvent) => {
            if (!menuRef.current?.contains(e.target as Node))
                setContextMenu(null);
        };
        const escape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setContextMenu(null);
        };
        document.addEventListener('pointerdown', dismiss);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('pointerdown', dismiss);
            document.removeEventListener('keydown', escape);
        };
    }, [contextMenu]);
    useEffect(() => {
        if (readOnly) setContextMenu(null);
    }, [readOnly]);
    const dragToMoveLabel = t('editors.route.constructorTab.dragToMove');

    useEffect(() => {
        let cancelled = false;
        let observer: ResizeObserver | undefined;
        void getRoutellersMapStyle(i18n.language)
            .then((style) => {
                if (cancelled || !containerRef.current) return;
                const map = new maplibregl.Map({
                    container: containerRef.current,
                    style: style as maplibregl.StyleSpecification,
                    center: [center.lng, center.lat],
                    zoom: 12,
                    maxZoom: 19,
                    dragRotate: false,
                    pitchWithRotate: false,
                    touchPitch: false,
                    maxPitch: 0,
                    attributionControl: { compact: true },
                });
                map.touchZoomRotate.disableRotation();
                map.addControl(
                    new maplibregl.NavigationControl({ showCompass: false }),
                    'top-left',
                );
                mapRef.current = map;
                map.on('error', () => {
                    if (!cancelled) setMapError(true);
                });
                map.on('load', () => {
                    if (cancelled) return;
                    map.addSource('constructor-route', {
                        type: 'geojson',
                        data: { type: 'FeatureCollection', features: [] },
                    });
                    map.addLayer({
                        id: 'constructor-route',
                        type: 'line',
                        source: 'constructor-route',
                        layout: { 'line-join': 'round', 'line-cap': 'round' },
                        paint: {
                            'line-color': '#2563EB',
                            'line-width': 4,
                            'line-opacity': 0.75,
                        },
                    });
                    setMapReady(true);
                });
                map.on('click', () => {
                    setContextMenu(null);
                    if (
                        draggingRef.current ||
                        Date.now() < suppressClickUntilRef.current
                    )
                        return;
                    callbacksRef.current.onClearSelection();
                });
                map.on('contextmenu', (e) => {
                    e.originalEvent.preventDefault();
                    if (callbacksRef.current.readOnly || draggingRef.current)
                        return;
                    setContextMenu({
                        x: Math.max(
                            8,
                            Math.min(
                                e.point.x,
                                map.getContainer().clientWidth - 184,
                            ),
                        ),
                        y: Math.max(
                            8,
                            Math.min(
                                e.point.y,
                                map.getContainer().clientHeight - 52,
                            ),
                        ),
                        lat: e.lngLat.lat,
                        lng: e.lngLat.lng,
                    });
                });
                map.on('movestart', () => setContextMenu(null));
                observer = new ResizeObserver(() => map.resize());
                observer.observe(containerRef.current);
            })
            .catch(() => {
                if (!cancelled) setMapError(true);
            });
        return () => {
            cancelled = true;
            observer?.disconnect();
            for (const marker of markersRef.current) marker.remove();
            markersRef.current = [];
            mapRef.current?.remove();
            mapRef.current = null;
            draggingRef.current = false;
            lastFitKeyRef.current = '';
            setMapReady(false);
        };
        // The constructor keeps its camera while editing; center is the initial city view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        // Selecting a waypoint during drag must not remove the marker being dragged.
        if (!mapReady || !map || draggingRef.current) return;
        for (const marker of markersRef.current) marker.remove();
        markersRef.current = [];
        const hitAttractions = new Set(highlightedPlaceKeys);
        const hitStops = new Set(highlightedStopKeys);
        const bounds = new maplibregl.LngLatBounds();
        const stopPlaceKeys = new Set(
            stops.filter((s) => s.kind === 'attraction').map((s) => s.placeKey),
        );

        const addMarker = (options: {
            lng: number;
            lat: number;
            size: number;
            color: string;
            label: string;
            selected: boolean;
            hit: boolean;
            dimmed: boolean;
            text?: string;
            select: () => void;
            waypointKey?: string;
            moveKind: 'stop' | 'place';
            moveKey: string;
            excluded?: boolean;
        }) => {
            const { selected, hit, dimmed, size, waypointKey } = options;
            const element = document.createElement('div');
            element.className = waypointKey
                ? 'route-waypoint-marker'
                : 'route-stop-marker';
            element.setAttribute('role', 'button');
            element.setAttribute('aria-label', options.label);
            element.tabIndex = 0;
            element.title = !readOnly
                ? `${options.label} — ${dragToMoveLabel}`
                : options.label;
            Object.assign(element.style, {
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '50%',
                background: options.color,
                color: '#fff',
                border: `3px solid ${selected ? '#93C5FD' : hit ? '#FDE68A' : '#fff'}`,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                font: '700 13px sans-serif',
                opacity: dimmed ? '0.25' : '1',
                cursor: !readOnly ? 'grab' : 'pointer',
                zIndex: selected
                    ? '30'
                    : hit
                      ? '20'
                      : options.text
                        ? '10'
                        : '1',
                boxShadow: selected
                    ? '0 0 0 3px #2563EB'
                    : hit
                      ? '0 0 0 3px #F59E0B'
                      : 'none',
            });
            element.textContent = options.text ?? '';
            if (options.excluded) {
                const warning = document.createElement('span');
                warning.textContent = '!';
                warning.title = 'Исключено при полевой проверке';
                Object.assign(warning.style, {
                    position: 'absolute',
                    right: '-8px',
                    top: '-8px',
                    background: '#FACC15',
                    color: '#713F12',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    textAlign: 'center',
                    lineHeight: '16px',
                });
                element.appendChild(warning);
            }
            if (selected || hit) {
                const label = document.createElement('span');
                label.textContent = options.label;
                Object.assign(label.style, {
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    background: hit && !selected ? '#FEF3C7' : '#fff',
                    color: '#111827',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    font: '600 11px/1.2 sans-serif',
                    pointerEvents: 'none',
                });
                element.appendChild(label);
            }
            element.addEventListener('click', (event) => {
                event.stopPropagation();
                if (
                    draggingRef.current ||
                    Date.now() < suppressClickUntilRef.current
                )
                    return;
                options.select();
            });
            element.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                options.select();
            });
            const marker = new maplibregl.Marker({
                element,
                draggable: !readOnly,
            })
                .setLngLat([options.lng, options.lat])
                .addTo(map);
            if (!readOnly) {
                marker.on('dragstart', () => {
                    draggingRef.current = true;
                    options.select();
                });
                marker.on('dragend', async () => {
                    const position = marker.getLngLat();
                    for (const item of markersRef.current)
                        item.setDraggable(false);
                    if (!callbacksRef.current.readOnly) {
                        try {
                            await callbacksRef.current.onMovePoint(
                                options.moveKind,
                                options.moveKey,
                                position.lat,
                                position.lng,
                            );
                        } finally {
                            marker.setLngLat([options.lng, options.lat]);
                        }
                    }
                    draggingRef.current = false;
                    suppressClickUntilRef.current = Date.now() + 250;
                    setDragRevision((value) => value + 1);
                });
            }
            markersRef.current.push(marker);
            if (hit) bounds.extend([options.lng, options.lat]);
        };

        for (const attraction of attractions) {
            if (attraction.lat == null || attraction.lng == null) continue;
            if (
                !isPoolVisible(attraction.reviewStatus, attraction.archived) ||
                stopPlaceKeys.has(attraction.placeKey)
            )
                continue;
            const selected = attraction.placeKey === selectedPlaceKey;
            const hit = searchActive && hitAttractions.has(attraction.placeKey);
            const dimmed = searchActive && !hit && !selected;
            addMarker({
                lng: attraction.lng,
                lat: attraction.lat,
                label: attraction.title,
                selected,
                hit,
                dimmed,
                size: selected ? 30 : hit ? 26 : dimmed ? 12 : 18,
                color: selected
                    ? '#3B82F6'
                    : hit
                      ? '#FBBF24'
                      : statusColor(attraction.reviewStatus),
                text: attraction.establishment ? '☕' : undefined,
                moveKind: 'place',
                moveKey: attraction.placeKey,
                select: () =>
                    callbacksRef.current.onSelectPlace(attraction.placeKey),
            });
        }
        for (const stop of stops) {
            const selected = stop.key === selectedStopKey;
            const hit =
                searchActive &&
                (hitStops.has(stop.key) ||
                    (stop.placeKey != null &&
                        hitAttractions.has(stop.placeKey)));
            const waypoint = stop.kind === 'waypoint';
            const text = stop.optionalBreak ? '☕' : String(stop.order + 1);
            addMarker({
                lng: stop.lng,
                lat: stop.lat,
                label: waypoint
                    ? `W${stop.order + 1}`
                    : `${text}. ${stop.title || `#${stop.placeKey}`}`,
                selected,
                hit,
                dimmed: searchActive && !hit && !selected,
                size: selected ? 38 : hit ? 36 : waypoint ? 28 : 30,
                color: selected
                    ? '#2563EB'
                    : hit
                      ? '#D97706'
                      : waypoint
                        ? '#4B5563'
                        : '#2563EB',
                text,
                excluded: stop.fieldExcluded,
                waypointKey: waypoint ? stop.key : undefined,
                moveKind: 'stop',
                moveKey: stop.key,
                select: () => callbacksRef.current.onSelectStop(stop.key),
            });
        }
        const source = map.getSource(
            'constructor-route',
        ) as maplibregl.GeoJSONSource;
        source.setData({
            type: 'FeatureCollection',
            features:
                geometry && geometry.length >= 2
                    ? [
                          {
                              type: 'Feature',
                              properties: {},
                              geometry: {
                                  type: 'LineString',
                                  coordinates: geometry,
                              },
                          },
                      ]
                    : [],
        });
        map.setPaintProperty(
            'constructor-route',
            'line-opacity',
            searchActive ? 0.35 : 0.75,
        );
        const fitKey = searchActive
            ? `${[...highlightedPlaceKeys].sort().join(',')}|${[...highlightedStopKeys].sort().join(',')}`
            : '';
        if (
            searchActive &&
            !bounds.isEmpty() &&
            fitKey !== lastFitKeyRef.current
        ) {
            lastFitKeyRef.current = fitKey;
            map.fitBounds(bounds, { maxZoom: 16, padding: 80, duration: 400 });
        } else if (!searchActive) lastFitKeyRef.current = '';
    }, [
        mapReady,
        attractions,
        stops,
        geometry,
        selectedPlaceKey,
        selectedStopKey,
        searchActive,
        highlightedPlaceKeys,
        highlightedStopKeys,
        readOnly,
        dragToMoveLabel,
        dragRevision,
    ]);

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: 480,
            }}
        >
            <div
                ref={containerRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#E8E9ED',
                }}
            />
            {contextMenu && !readOnly && (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Действия на карте"
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                        position: 'absolute',
                        left: contextMenu.x,
                        top: contextMenu.y,
                        zIndex: 10,
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 2px 12px #0003',
                        padding: 4,
                    }}
                >
                    <button
                        type="button"
                        role="menuitem"
                        autoFocus
                        style={{
                            border: 0,
                            borderRadius: 4,
                            padding: '10px 16px',
                            background: '#fff',
                            color: '#1c7ed6',
                            cursor: 'pointer',
                            font: 'inherit',
                        }}
                        onClick={() => {
                            const point = contextMenu;
                            setContextMenu(null);
                            if (!callbacksRef.current.readOnly)
                                callbacksRef.current.onAddWaypoint(
                                    point.lat,
                                    point.lng,
                                );
                        }}
                    >
                        Добавить точку
                    </button>
                </div>
            )}
            {mapError && (
                <div
                    role="alert"
                    style={{
                        position: 'absolute',
                        top: 12,
                        left: 48,
                        right: 12,
                        padding: 8,
                        background: '#fff',
                        borderRadius: 8,
                    }}
                >
                    {t('editors.route.constructorTab.mapLoadError', {
                        defaultValue:
                            'Не удалось загрузить карту полностью. Проверьте соединение и откройте карту заново.',
                    })}
                </div>
            )}
        </div>
    );
};
